// The sidebar of a settings screen (Me's settings, a workspace's settings and
// the operator's place use this one). The same rows as every sidebar: a way
// back at the top, then the settings' name (it opens the index: every item
// with its sentence) with the place under it when there is one (the
// workspace's name), a search that narrows the items, and the items with their
// icons in their groups. While searching, the sections of an item that match
// show under it, one step in.
//
// Promise: it folds to its icons (`folded`, built from the same folded column
// as Me's) only where the window is too narrow for it, never by hand (its
// frame folds by width only), so every item stays one press away at any width
// where Me's sidebar is in the row. Folded, the way back is the close icon,
// the settings' name is the settings icon (to the index), the search is an
// icon that opens the full sidebar over the panel, a group's heading is a
// thin rule, and the current item has the current row's ground. Every icon
// sits where the full sidebar has it, so nothing moves as the full sidebar
// grows out of the column (a peek, the drawer) or folds into it.
//
// Promise: unfolded, it is as wide as its longest row (every item's name with
// its icon, the way back, the settings' name and place) and no wider — it
// tells the frame that width (`fitSidebar`), measured from the rows as drawn,
// so it follows the language; the frame keeps it between the sidebar's
// least and greatest width, and its edge only folds and unfolds it.
import { Link } from "@tanstack/react-router";
import { m } from "@trameria/i18n";
import { type ComponentType, useLayoutEffect, useRef, useState } from "react";
import { Icon, iconOf } from "../ui/icon";
import { Tooltip } from "../ui/tooltip";
import {
  CollapsedColumn,
  ICON_ROW,
  IconDrawerRow,
  IconNavRow,
} from "./collapsed-sidebar";
import { useShellFrame } from "./frame";
import { NavRow, ROW, SectionHeading } from "./nav-row";

export interface SettingsNavItem {
  id: string;
  /** Where the item's page is. */
  to: string;
  label(): string;
  /** The item's mark (all there is of it when folded). */
  icon: ComponentType<{ className?: string }>;
  /** Matched by the search as well as the label. */
  description?(): string;
  /** Anchors inside the item (`#<id>`), found by the search. */
  sections?: ReadonlyArray<{ id: string; label(): string }>;
  /** A row that opens something else (the app's settings window) instead of `to`. */
  open?(): void;
}

export interface SettingsNavGroup {
  /** A heading over the group (none for a single group). */
  label?(): string;
  items: readonly SettingsNavItem[];
}

interface SettingsSidebarProps {
  /** The name of the navigation, for screen readers. */
  label: string;
  /** The settings' name, at the top (opens the index). */
  title: string;
  /** Whose settings, under the name (a workspace's name). */
  subtitle?: string;
  /** The index of every item. */
  indexTo: string;
  onBack(): void;
  groups: readonly SettingsNavGroup[];
  /** Folded to its icons (the frame's `collapsed`). */
  folded?: boolean;
}

const matches = (text: string | undefined, q: string) =>
  text !== undefined && text.toLowerCase().includes(q.trim().toLowerCase());

const SettingsIcon = iconOf("settings");

export function SettingsSidebar({
  folded = false,
  ...props
}: SettingsSidebarProps) {
  return (
    <>
      <FitWidth {...props} />
      {folded ? (
        <FoldedSettingsSidebar {...props} />
      ) : (
        <FullSettingsSidebar {...props} />
      )}
    </>
  );
}

/**
 * Text that takes its room but is not in the page's text (a generated
 * content), so the unseen copy is never found by a search or a query.
 */
const Text = ({ value }: { value: string }) => (
  <span data-text={value} className="before:content-[attr(data-text)]" />
);

/** The full sidebar's padding at each side (`px-2`). */
const SIDE_PADDING = 8;

/**
 * The full sidebar's rows drawn unseen at their natural width; the widest,
 * with the sidebar's padding, is told to the frame as the sidebar's width.
 */
function FitWidth({ title, subtitle, groups }: SettingsSidebarProps) {
  const { fitSidebar } = useShellFrame();
  const rows = useRef<HTMLDivElement>(null);
  const [key] = useState(() => ({}));
  // Measuring what is drawn, before it is painted: the width is known from
  // the first frame, and again whenever the rows' width changes.
  useLayoutEffect(() => {
    const el = rows.current;
    if (!el) return;
    // Rounded up: a name a fraction of a pixel short would be cut.
    const tell = () =>
      fitSidebar(
        key,
        Math.ceil(el.getBoundingClientRect().width) + 2 * SIDE_PADDING,
      );
    tell();
    const sizes =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(tell);
    sizes?.observe(el);
    return () => {
      sizes?.disconnect();
      fitSidebar(key, null);
    };
  }, [fitSidebar, key]);
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none invisible absolute top-0 left-0 h-0 overflow-hidden"
    >
      {/* A scroll box like the list's: its room for a scrollbar is counted. */}
      <div
        ref={rows}
        data-sidebar-fit=""
        className="flex w-max flex-col overflow-y-auto"
      >
        <div className={ROW}>
          <Icon kind="close" />
          <Text value={m.shell_settings_back()} />
        </div>
        <div className={ROW}>
          <Text value={title} />
        </div>
        {subtitle && (
          <div className="px-2.5 text-[11px] leading-4">
            <Text value={subtitle} />
          </div>
        )}
        {groups.map((g, i) => (
          <div key={g.label?.() ?? i} className="flex flex-col">
            {g.label && (
              <SectionHeading>
                <Text value={g.label()} />
              </SectionHeading>
            )}
            {g.items.map((item) => (
              <div key={item.id} className={ROW}>
                {/* As the row draws it (an item from older code may lack one). */}
                {item.icon && <item.icon />}
                <Text value={item.label()} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function FullSettingsSidebar({
  label,
  title,
  subtitle,
  indexTo,
  onBack,
  groups,
}: SettingsSidebarProps) {
  const frame = useShellFrame();
  const [q, setQ] = useState("");
  const query = q.trim();
  const shown = groups
    .map((g) => ({
      label: g.label,
      items: g.items.flatMap((item) => {
        if (!query) return [{ item, hits: [] }];
        const hits = (item.sections ?? []).filter((s) =>
          matches(s.label(), query),
        );
        if (
          hits.length === 0 &&
          !matches(item.label(), query) &&
          !matches(item.description?.(), query)
        )
          return [];
        return [{ item, hits }];
      }),
    }))
    .filter((g) => g.items.length > 0);
  return (
    <nav
      aria-label={label}
      className="flex h-full min-h-0 flex-col bg-sidebar px-2 pt-2.5"
    >
      {/* In the 36 px the folded column gives its head (Me's switcher row),
          so the close icon stays put as the sidebar folds and unfolds. */}
      <div className="flex h-9 flex-none items-center gap-0.5">
        <button
          type="button"
          className={`${ROW} min-w-0 flex-1 cursor-pointer text-left`}
          onClick={onBack}
        >
          <Icon kind="close" className="text-sidebar-muted" />
          <span data-fold-label="" className="min-w-0 flex-1 truncate">
            {m.shell_settings_back()}
          </span>
        </button>
        {/* Room for the lock the frame lays over this end. */}
        {frame.lockInRow && (
          <div aria-hidden="true" className="w-9 flex-none" />
        )}
      </div>
      {/* The settings' name: not a nav row (no current ground), the way to the
          index. The old web's head: the name, and whose settings under it. */}
      <Link
        to={indexTo}
        activeOptions={{ exact: true, includeSearch: false }}
        activeProps={{ "aria-current": "page" }}
        className={`${ROW} mt-2 flex-none text-sidebar-fg-strong`}
      >
        <span data-fold-label="" className="min-w-0 flex-1 truncate">
          {title}
        </span>
      </Link>
      {subtitle && (
        <div
          data-fold-label=""
          className="-mt-1 h-5 flex-none truncate px-2.5 pb-1 text-[11px] leading-4 text-sidebar-muted"
        >
          {subtitle}
        </div>
      )}
      <div data-fold-label="" className="mt-2 flex-none px-0.5">
        <input
          type="search"
          aria-label={m.shell_settings_search()}
          placeholder={m.shell_settings_search()}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="plain-field h-8 w-full rounded-[7px] border border-border bg-surface px-2.5 text-[12.5px] text-fg outline-none focus:border-field-strong"
        />
      </div>
      <div className="mt-1 flex min-h-0 flex-1 flex-col gap-px overflow-y-auto pb-4">
        {shown.length === 0 && (
          <p
            data-fold-label=""
            className="px-2.5 py-2 text-[12.5px] text-sidebar-muted"
          >
            {m.shell_settings_no_match()}
          </p>
        )}
        {shown.map((g, i) => (
          <div key={g.label?.() ?? i} className="flex flex-col gap-px">
            {g.label ? (
              <SectionHeading>{g.label()}</SectionHeading>
            ) : (
              <div className="h-1" />
            )}
            {g.items.map(({ item, hits }) => (
              <div key={item.id} className="flex flex-col gap-px">
                {item.open ? (
                  <OpenRow item={item} />
                ) : (
                  <NavRow to={item.to} icon={item.icon} label={item.label()} />
                )}
                {hits.map((s) => (
                  // Under the item's name, past its icon.
                  <Link
                    key={s.id}
                    to={item.to}
                    hash={s.id}
                    data-fold-label=""
                    className={`${ROW} pl-9`}
                  >
                    {s.label()}
                  </Link>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </nav>
  );
}

function FoldedSettingsSidebar({
  label,
  title,
  subtitle,
  indexTo,
  onBack,
  groups,
}: SettingsSidebarProps) {
  const back = m.shell_settings_back();
  return (
    <CollapsedColumn
      label={label}
      head={
        <Tooltip text={back} side="right">
          <button
            type="button"
            aria-label={back}
            className={ICON_ROW}
            onClick={onBack}
          >
            <Icon kind="close" />
          </button>
        </Tooltip>
      }
    >
      {/* Where the full sidebar has the settings' name, (the place,) and the
          search: they fade out as the full sidebar grows over them. */}
      <div className="flex flex-col items-center">
        <div data-fold-only="">
          <IconNavRow to={indexTo} icon={SettingsIcon} label={title} exact />
        </div>
        {subtitle && <div aria-hidden="true" className="h-4" />}
        <div className="mt-2">
          <IconDrawerRow
            icon={<Icon kind="search" className="pointer-events-none" />}
            label={m.shell_settings_search()}
          />
        </div>
      </div>
      {/* 1 px of the column's gap and 3: the full sidebar's 4 above its list. */}
      <div className="mt-[3px] flex flex-col items-center gap-px">
        {groups.map((g, i) => (
          <div
            key={g.label?.() ?? i}
            className="flex flex-col items-center gap-px"
          >
            {g.label ? <IconGroupRule /> : <div className="h-1" />}
            {g.items.map((item) =>
              item.open ? (
                <IconOpenRow key={item.id} item={item} />
              ) : (
                <IconNavRow
                  key={item.id}
                  to={item.to}
                  icon={item.icon}
                  label={item.label()}
                />
              ),
            )}
          </div>
        ))}
      </div>
    </CollapsedColumn>
  );
}

/** A row that opens something else (never the current place). */
function OpenRow({ item }: { item: SettingsNavItem }) {
  const open = item.open;
  return (
    <button
      type="button"
      className={`${ROW} cursor-pointer text-left`}
      onClick={() => open?.()}
    >
      <item.icon className="text-sidebar-muted" />
      <span data-fold-label="" className="min-w-0 flex-1 truncate">
        {item.label()}
      </span>
    </button>
  );
}

/** The same folded to its icon. */
function IconOpenRow({ item }: { item: SettingsNavItem }) {
  const label = item.label();
  return (
    <Tooltip text={label} hold>
      <button
        type="button"
        aria-label={label}
        className={ICON_ROW}
        onClick={() => item.open?.()}
      >
        <item.icon className="pointer-events-none" />
      </button>
    </Tooltip>
  );
}

/** A group's heading folded: a short rule, as tall as the heading. */
const IconGroupRule = () => (
  <div
    aria-hidden="true"
    className="mt-5 mb-1 flex h-4 w-9 items-center justify-center"
  >
    <span className="h-px w-4 bg-border" />
  </div>
);
